import React from "react";
import TextInput from "@elements/fields/textInput";
import { IGiftFinderQuestion } from "@types";
import ImageSelection from "./ImageSelection";
import MultiSelection from "./MultiSelection";
import SingleSelection from "./SingleSelection";
import TextInputStep from "./TextInput";

interface Props {
    question: IGiftFinderQuestion;
    answer: any[];
    extraAnswer?: string;
    onAnswerChange: (value: any[]) => void;
    onExtraAnswerChange: (value: string) => void;
}

const normalizeType = (type: string = "") => type.toLowerCase().replace(/[\s_]+/g, "");

const QuestionStep: React.FC<Props> = ({
    question,
    answer,
    extraAnswer,
    onAnswerChange,
    onExtraAnswerChange,
}) => {
    const normalizedType = normalizeType(question.type);
    const isMulti = normalizedType.includes("multi");
    const extraQuestion = question.extra_question || null;
    const extraQuestionInputId = `gift-finder-extra-${question.id}`;

    return (
        <div className="flex flex-col gap-4">
            <div>
                <h2 className="text-lg font-bold text-[#4F2F18]">{question.title}</h2>
                {question.description ? (
                    <p className="text-sm text-gray-600 mt-1">{question.description}</p>
                ) : null}
            </div>

            <div>
                {normalizedType.includes("image") ? (
                    <ImageSelection
                        options={question.options || []}
                        value={answer}
                        onChange={onAnswerChange}
                        isMulti={isMulti}
                    />
                ) : null}

                {normalizedType.includes("multi") && !normalizedType.includes("image") ? (
                    <MultiSelection
                        options={question.options || []}
                        value={answer}
                        onChange={onAnswerChange}
                        multiSelectLimit={question.multi_select_limit}
                    />
                ) : null}

                {normalizedType.includes("single") && !normalizedType.includes("image") ? (
                    <SingleSelection
                        options={question.options || []}
                        value={answer}
                        onChange={onAnswerChange}
                    />
                ) : null}

                {normalizedType.includes("text") ? (
                    <TextInputStep
                        value={typeof answer?.[0] === "string" ? answer[0] : ""}
                        placeholder={question.hint}
                        onChange={(value) => onAnswerChange(value ? [value] : [])}
                    />
                ) : null}
            </div>

            {extraQuestion ? (
                <div className="pt-2">
                    {extraQuestion.title ? (
                        <label
                            htmlFor={extraQuestionInputId}
                            className="mb-2 block text-sm font-medium text-[#4F2F18]"
                        >
                            {extraQuestion.title}
                        </label>
                    ) : null}
                    <TextInput
                        id={extraQuestionInputId}
                        name={extraQuestionInputId}
                        label=""
                        required={!extraQuestion.is_skippable}
                        placeholder={extraQuestion.hint}
                        value={extraAnswer || undefined}
                        onChange={(event) => onExtraAnswerChange(event.target.value)}
                        className="[&_input]:bg-white"
                    />
                </div>
            ) : null}
        </div>
    );
};

export default React.memo(QuestionStep);
